import { Color, Size, t } from '@bms2/shared';
import { Button, Checkbox } from '../../components/controls';
import styles from './QuoteRecipients.module.css';

/** One contact of the Account as the form lists it (T922; decisions 1750, 1860); `address` is null where the contact has no work email. */
export interface RecipientChoice {
  personId: string;
  name: string;
  address: string | null;
  ticked: boolean;
}

/**
 * 🔒 The Quote's Recipients block (T922; decisions 1750, 1860): the Account's
 * contacts as tick boxes, Select all, and Add new. A contact with no address is
 * tickable and reads "no address" beside the name, because a quote handed over
 * by hand needs nobody's email; Mark as sent is the way out, and the block
 * carries no refusal.
 */
export function QuoteRecipients({ contacts, ticked, onChange, onAddNew }: { contacts: readonly RecipientChoice[]; ticked: ReadonlySet<string>; onChange: (next: Set<string>) => void; onAddNew: () => void }) {
  const toggle = (id: string, on: boolean) => {
    const next = new Set(ticked);
    if (on) next.add(id);
    else next.delete(id);
    onChange(next);
  };
  return (
    <div className={styles.block} data-quote-recipients="true">
      <p className={styles.heading}>{t('quote.recipients.heading')}</p>
      {contacts.length === 0 ? <p className={styles.hint}>{t('quote.recipients.none')}</p> : null}
      <ul className={styles.list}>
        {contacts.map((contact) => {
          const label = [contact.name, contact.address ?? t('quote.recipients.noAddress')].join(' · ');
          return (
            <li key={contact.personId} data-recipient={contact.personId} data-recipient-address={contact.address ? 'true' : 'none'}>
              <Checkbox
                size={Size.Medium}
                label={label}
                checked={ticked.has(contact.personId)}
                onChange={(on) => toggle(contact.personId, on)}
              />
            </li>
          );
        })}
      </ul>
      <div className={styles.actions}>
        <Button size={Size.Small} color={Color.Inactive} text={[t('quote.recipients.selectAll')]} actions={[() => onChange(new Set(contacts.map((c) => c.personId)))]} disabled={contacts.length === 0} />
        <Button size={Size.Small} color={Color.Inactive} text={[t('quote.recipients.addNew')]} actions={[onAddNew]} />
      </div>
    </div>
  );
}
